import React, { useState } from "react";
import { Avatar, Divider, Menu, MenuItem, Select, styled } from "@mui/material";
import {
  LanguageRounded,
  LogoutRounded,
  Person2,
  KeyboardArrowDownRounded,
  GTranslateRounded,
} from "@mui/icons-material";
import { useRouter } from "next/router";
import { getCurrentUser, logout } from "@/lib/auth.util";
import { useTranslation } from "next-i18next";
import useAuthStore from "@/store/Auth/useAuthStore";

const DashboardHeader: React.FC<{ open: boolean; isMobile: boolean }> = ({
  open,
  isMobile,
}) => {
  const { t, i18n } = useTranslation("common");
  const currentUser = getCurrentUser();
  const router = useRouter();

  // User menu state
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const isUserMenuOpen = Boolean(userMenuAnchorEl);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleChangeLang = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    router.push(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { locale: newLang }
    );
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    router.push("/login");
  };

  const getWelcomeMessage = () => {
    const userName = currentUser?.Name;
    if (i18n.language === "ar") {
      return {
        greeting: t("dashboard.header.greeting", { name: userName }),
        description: t("dashboard.header.description"),
      };
    } else {
      return {
        greeting: t("dashboard.header.greeting", { name: userName }),
        description: t("dashboard.header.description"),
      };
    }
  };

  const welcomeMessage = getWelcomeMessage();

  return (
    <div
      className={`${
        open
          ? isMobile
            ? "w-full"
            : "w-[calc(100%-20rem)] "
          : isMobile
          ? "w-full"
          : "w-[calc(100%-5rem)] "
      } h-[7rem] flex flex-col mb-4  ml-auto`}
    >
      <div
        className={` ${
          isMobile ? "hidden" : "flex"
        }  flex-row-reverse items-center justify-between flex-1 px-[1rem] `}
      >
        {/* Combined User Profile and Language Selector Pill */}
        <div className="flex items-center gap-[1rem]">
          <div className="bg-gray-100 rounded-full px-6 py-3 flex items-center gap-4 shadow-sm">
            {/* User Profile Section */}

            <div className="flex items-center gap-2">
              <button
                onClick={handleChangeLang}
                className="bg-transparent text-md text-gray-800 gap-3 flex-row flex items-center justify-center font-bold min-w-[5rem] px-4 py-2 rounded-full hover:bg-gray-200 transition-colors"
              >
                <GTranslateRounded className="text-black text-xl" />
                {i18n.language === "ar" ? "English" : "عربي"}
              </button>
            </div>
            {/* Separator */}
            <div className="w-px h-6 bg-gray-300"></div>

            {/* Language Selection */}
            <div
              onClick={handleUserMenuOpen}
              className="flex items-center gap-3 cursor-pointer hover:bg-gray-200 transition-colors rounded-full px-4 py-2"
            >
              <KeyboardArrowDownRounded className="text-gray-500 text-lg" />
              <span className="font-bold text-gray-800 text-md">
                {currentUser?.Name}
              </span>
              <Avatar
                className="w-8 h-8 cursor-pointer hover:bg-gray-200 bg-primary transition-colors border-2 border-primary/50 "
                onClick={handleUserMenuOpen}
              >
                <Person2 className="text-secondary" />
              </Avatar>
            </div>
          </div>
        </div>

        {/* Welcome Message */}
        <div>
          <div className="text-xl font-bold text-gray-800 mb-1 gap-2 flex">
            {t("dashboard.header.hello")},
            <span className="font-bold text-gray-800 text-md">
              {currentUser?.Name}
            </span>
          </div>
          <div className="text-md text-gray-600 mt-2">
            {t("dashboard.header.whatTasks")}
          </div>
        </div>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchorEl}
          open={isUserMenuOpen}
          onClose={handleUserMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              borderRadius: "0.5rem",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <MenuItem
            onClick={handleLogout}
            className="flex items-center gap-[0.5rem] rounded-[0.5rem]"
          >
            <LogoutRounded className="text-[1.2rem]" />
            {t("dashboard.nav.logout")}
          </MenuItem>
        </Menu>
      </div>
      <Divider />
    </div>
  );
};

export default DashboardHeader;
